import { useEffect, useState } from 'react';
import styled, { withTheme } from 'styled-components';
import { VictoryArea, VictoryAxis, VictoryBar, VictoryChart, VictoryTooltip, VictoryVoronoiContainer } from 'victory';

import hasRate from '../../../utils/hasRate';
import { addRateCategoriesToRecords, RateCategories } from '../../../utils/savingsUtils';

const LegendRow = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-top: 4px;
  margin-bottom: 8px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: ${(props) => props.theme.colors.dark};
`;

const ColorSwatch = styled.div`
  width: 12px;
  height: 12px;
  background-color: ${(props) => props.$color};
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
`;

/**
 * Builds rate shading blocks from already-categorised chart data.
 * Mirrors buildRateShadingBlocksFromCostWindows but uses _rate_category fields
 * that addRateCategoriesToRecords adds to each point.
 */
const buildShadingBlocks = (data, direction, maxY) => {
  const key = `${direction}_rate_category`;
  const result = { day: [], cheap: [], peak: [] };

  [RateCategories.DAY, RateCategories.CHEAP, RateCategories.PEAK].forEach((targetCategory) => {
    const blockKey =
      targetCategory === RateCategories.CHEAP ? 'cheap' : targetCategory === RateCategories.PEAK ? 'peak' : 'day';
    let currentBlock = [];

    data.forEach((point, index) => {
      const category = point[key];
      const isLast = index === data.length - 1;
      if (category === targetCategory) {
        currentBlock.push({ x: point._x, y: maxY, y0: 0 });
      }
      const atEnd = category !== targetCategory || isLast;
      if (atEnd && currentBlock.length > 0) {
        if (category !== targetCategory) {
          currentBlock.push({ x: point._x, y: maxY, y0: 0 });
        } else if (isLast) {
          // Extend final stepAfter segment through the last bucket.
          currentBlock.push({ x: point._x + 1, y: maxY, y0: 0 });
        }
        if (currentBlock.length === 1) currentBlock.push({ ...currentBlock[0] });
        result[blockKey].push(currentBlock);
        currentBlock = [];
      }
    });
  });

  return result;
};

const Chart = ({ data, dataKey, barColor, yAxisLabel, xAxisLabel, dataAvailable, direction, theme }) => {
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (data?.length) {
      const sorted = [...data].sort((a, b) => {
        const aStart = new Date(a.start_date || 0).getTime();
        const bStart = new Date(b.start_date || 0).getTime();
        return aStart - bStart;
      });
      const dataWithCategories = addRateCategoriesToRecords(sorted, direction);
      setChartData(dataWithCategories);
    }
  }, [data, direction]);

  // Convert pence to pounds; add sequential index _x for stable chronological x-positioning
  const chartDataInPounds = chartData?.map((row, i) => ({ ...row, [dataKey]: row[dataKey] / 100, _x: i }));

  const highestDayEarnings = chartDataInPounds?.reduce((acc, row) => (row[dataKey] > acc ? row[dataKey] : acc), 0) ?? 0;
  const domainMax = dataAvailable && highestDayEarnings > 0 ? highestDayEarnings * 1.3 : 4;

  const shadingBlocks =
    dataAvailable && chartDataInPounds
      ? buildShadingBlocks(chartDataInPounds, direction, domainMax)
      : { day: [], cheap: [], peak: [] };

  const hasCheap = hasRate(chartData, direction, RateCategories.CHEAP);
  const hasPeak = hasRate(chartData, direction, RateCategories.PEAK);

  return (
    <div style={{ position: 'relative', aspectRatio: '648 / 420' }}>
      <VictoryChart
        width={648}
        height={420}
        padding={{ top: 30, right: 50, bottom: 71, left: 60 }}
        domain={{ x: [-0.5, 47.5], y: [0, domainMax] }}
        containerComponent={
          <VictoryVoronoiContainer
            labels={({ datum }) => {
              if (datum[dataKey]) {
                return `£${datum[dataKey].toFixed(2)}`;
              }
            }}
            labelComponent={
              <VictoryTooltip
                cornerRadius={4}
                pointerLength={10}
                style={{ fontSize: 16 }}
                flyoutStyle={{
                  fill: theme.colors.white,
                  stroke: theme.colors.extraLight,
                }}
                flyoutPadding={{ top: 4, bottom: 5, left: 8, right: 8 }}
              />
            }
          />
        }
      >
        <VictoryAxis
          label={xAxisLabel}
          tickValues={Array.from({ length: 24 }, (_, i) => i * 2)}
          tickFormat={(v) => (v % 6 === 0 ? String(Math.floor(v / 2)).padStart(2, '0') : '')}
          style={{
            axis: { stroke: theme.colors.light },
            axisLabel: {
              fontSize: 16,
              padding: 50,
            },
            ticks: { stroke: theme.colors.light, size: 4 },
            tickLabels: {
              fontSize: 14,
              padding: 5,
            },
            grid: { stroke: 'transparent' },
          }}
          offsetY={70}
        />
        <VictoryAxis
          dependentAxis
          label={`${yAxisLabel} (£)`}
          tickFormat={(v) => v.toFixed(2)}
          style={{
            axis: { stroke: 'transparent' },
            ticks: { stroke: 'transparent' },
            tickLabels: {
              fill: theme.colors.black,
              fontSize: 14,
              padding: 5,
            },
            grid: {
              stroke: theme.colors.light,
              opacity: 0.3,
              strokeWidth: 0.5,
              strokeDasharray: '0',
            },
            axisLabel: {
              fontSize: 16,
              padding: 40,
              textAnchor: 'middle',
              fontWeight: 'bold',
              fill: barColor,
            },
          }}
        />
        {/* Rate shading background: cheap (green) and peak (yellow) only; day rate = clear */}
        {shadingBlocks.cheap.map((block, i) => (
          <VictoryArea
            key={`cheap-${i}`}
            data={block}
            interpolation="stepAfter"
            style={{ data: { fill: theme.colors.cheap, fillOpacity: 1, stroke: 'none' } }}
          />
        ))}
        {shadingBlocks.peak.map((block, i) => (
          <VictoryArea
            key={`peak-${i}`}
            data={block}
            interpolation="stepAfter"
            style={{ data: { fill: theme.colors.peak, fillOpacity: 1, stroke: 'none' } }}
          />
        ))}
        {/* Earnings bars on top of shading */}
        <VictoryBar
          data={dataAvailable && chartDataInPounds ? chartDataInPounds : []}
          x="_x"
          y={dataKey}
          barWidth={10}
          style={{ data: { fill: barColor } }}
        />
      </VictoryChart>
      {/* HTML legend below chart */}
      {(hasCheap || hasPeak) && (
        <LegendRow>
          {hasCheap && (
            <LegendItem>
              <ColorSwatch $color={theme.colors.cheap} />
              Cheap rate
            </LegendItem>
          )}
          {hasPeak && (
            <LegendItem>
              <ColorSwatch $color={theme.colors.peak} />
              Peak rate
            </LegendItem>
          )}
        </LegendRow>
      )}
    </div>
  );
};

export default withTheme(Chart);
